import axiosInstance from '../../api/axios';

// PBCRequest mirrors skeptics-backend/models/report_models.go's `PBCRequest`
// JSON tags verbatim (Feature 5.3, Controller report #29). Do not rename
// fields without updating both sides of the contract.
//
// FIELD TRAP: the embedded `BaseOrganizationScoped.OrganizationID` field has
// NO json tag on the Go side, so it serializes as PascalCase `OrganizationID`
// (not `organization_id`) — inconsistent with every other field on this
// struct. The UI never needs it (org scoping is implicit via the session),
// so it's typed here but intentionally unused.
export type PBCRequest = {
  id: string;
  OrganizationID?: string;
  sox_control_id: string;
  requested_by_user_id?: number | null;
  assigned_to_user_id?: number | null;
  title: string;
  description?: string | null;
  due_date?: string | null; // "YYYY-MM-DD"
  status: PBCStatus;
  control_evidence_id?: number | null;
  /** Testing period the PBC evidence request covers (feedback round 2026-08, migration 000106). Both nullable. */
  testing_period_start?: string | null; // "YYYY-MM-DD"
  testing_period_end?: string | null; // "YYYY-MM-DD"
  created_at: string;
  updated_at: string;
  /** Display names (feedback round 2026-08-23) — populated server-side via a
   * LEFT JOIN on `users` in pbc_repository.go's ListByOrg; null if the user
   * lookup misses (e.g. deleted user) or the request is unassigned. */
  assigned_to_name?: string | null;
  requested_by_name?: string | null;
};

export type PBCStatus = 'pending' | 'received' | 'rejected' | 'overdue';

export const PBC_STATUSES: PBCStatus[] = ['pending', 'received', 'rejected', 'overdue'];

export type PBCListParams = {
  control_id?: string;
  status?: string;
  audit_year_id?: string;
};

export type PBCCreateBody = {
  sox_control_id: string;
  title: string;
  description?: string;
  due_date?: string; // "YYYY-MM-DD"
  assigned_to_user_id?: number;
  status?: PBCStatus;
  audit_year_id?: string;
  testing_period_start?: string | null;
  testing_period_end?: string | null;
};

export type PBCUpdateBody = {
  title?: string;
  description?: string | null;
  due_date?: string | null;
  assigned_to_user_id?: number | null;
  control_evidence_id?: number | null;
  status?: PBCStatus;
  testing_period_start?: string | null;
  testing_period_end?: string | null;
};

type RowsEnvelope<T> = { rows: T[] };

const BASE = '/api/pbc';

export const fetchPBCRequests = async (params: PBCListParams = {}): Promise<PBCRequest[]> => {
  const query: Record<string, string> = {};
  if (params.control_id) query.control_id = params.control_id;
  if (params.status) query.status = params.status;
  if (params.audit_year_id) query.audit_year_id = params.audit_year_id;
  const response = await axiosInstance.get<RowsEnvelope<PBCRequest>>(BASE, { params: query });
  return response?.data?.rows ?? [];
};

export const createPBCRequest = async (body: PBCCreateBody): Promise<PBCRequest> => {
  const response = await axiosInstance.post<PBCRequest>(BASE, body);
  return response.data;
};

export const updatePBCStatus = async (id: string, status: PBCStatus): Promise<PBCRequest> => {
  const response = await axiosInstance.put<PBCRequest>(`${BASE}/${encodeURIComponent(id)}/status`, { status });
  return response.data;
};

export const updatePBCRequest = async (id: string, body: PBCUpdateBody): Promise<PBCRequest> => {
  const response = await axiosInstance.put<PBCRequest>(`${BASE}/${encodeURIComponent(id)}`, body);
  return response.data;
};

/** Minimal control option for the "New PBC Request" control picker. Sourced from
 * GET /api/sox-controls — pbc_requests only stores the raw sox_control_id UUID
 * (unlike assigned_to/requested_by, which GET /api/pbc now resolves to display
 * names via a join; see pbc_repository.go's ListByOrg). */
export type PBCControlOption = {
  id: string;
  control_id: string;
  title: string;
};

export const fetchPBCControlOptions = async (): Promise<PBCControlOption[]> => {
  const response = await axiosInstance.get('/api/sox-controls', { params: { page: 1, page_size: 200 } });
  const body = response.data ?? {};
  const root = body.data && typeof body.data === 'object' ? body.data : body;
  const list: unknown[] = Array.isArray(root?.controls)
    ? root.controls
    : Array.isArray(root?.items)
      ? root.items
      : Array.isArray(root)
        ? root
        : [];
  return list.map((raw) => {
    const row = raw as Record<string, unknown>;
    return {
      id: String(row.id ?? ''),
      control_id: String(row.control_id ?? row.control_display_id ?? ''),
      title: String(row.title ?? row.control_name ?? ''),
    };
  }).filter((row) => row.id);
};
